import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../auth/api";
import { alertSuccess, alertError } from "../components/Alert";

// Fetch VType data
export const fetchVTypeData = createAsyncThunk(
  "panel/fetchVTypeData",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/panel/VType");
      return response.data; // Adjust based on your API response structure
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Update VType data
export const updateVTypeData = createAsyncThunk(
  "panel/updateVTypeData",
  async (VTypeData, { rejectWithValue }) => {
    try {
      const response = await api.patch("/panel/VType", VTypeData);
      alertSuccess(response.data.success);
      return response.data; // Adjust based on your API response structure
    } catch (error) {
      alertError(error.response?.data.errors);
      return rejectWithValue(error.response?.data.errors || error.message);
    }
  }
);

export const addVTypeData = createAsyncThunk(
  "panel/addVTypeData",
  async (VTypeData, { rejectWithValue }) => {
    try {
      const response = await api.post("/panel/VType", VTypeData);
      alertSuccess(response.data.success);
      return response.data; // Adjust based on your API response structure
    } catch (error) {
      alertError(error.response?.data.errors);
      return rejectWithValue(error.response?.data.errors || error.message);
    }
  }
);

// Fetch Category data
export const fetchCategoryData = createAsyncThunk(
  "panel/fetchCategoryData",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/panel/Category");
      return response.data; // Adjust based on your API response structure
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Update Category data
export const updateCategoryData = createAsyncThunk(
  "panel/updateCategoryData",
  async (categoryData, { rejectWithValue }) => {
    try {
      const response = await api.patch("/panel/Category", categoryData);
      alertSuccess(response.data.success);
      return response.data; // Adjust based on your API response structure
    } catch (error) {
      alertError(error.response?.data.errors);
      return rejectWithValue(error.response?.data.errors || error.message);
    }
  }
);


// Add new Category data
export const addCategoryData = createAsyncThunk(
  "panel/addCategoryData",
  async (newCategoryData, { rejectWithValue }) => {
    try {
      const response = await api.post("/panel/Category", newCategoryData);
      alertSuccess(response.data.success);
      return response.data; // Adjust based on your API response structure
    } catch (error) {
      alertError(error.response?.data.errors);
      return rejectWithValue(error.response?.data.errors || error.message);
    }
  }
);

export const fetchSubCategoryData = createAsyncThunk(
  "panel/fetchSubCategoryData",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/panel/SubCategory");
      return response.data; // Adjust based on your API response structure
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateSubCategoryData = createAsyncThunk(
  "panel/updateSubCategoryData",
  async (subCategoryData, { rejectWithValue }) => {
    try {
      const response = await api.patch("/panel/SubCategory", subCategoryData);
      alertSuccess(response.data.success);
      return response.data; // Adjust based on your API response structure
    } catch (error) {
      alertError(error.response?.data.errors);
      return rejectWithValue(error.response?.data.errors || error.message);
    }
  }
);

export const addSubCategoryData = createAsyncThunk(
  "panel/addSubCategoryData",
  async (newSubCategoryData, { rejectWithValue }) => {
    try {
      const response = await api.post("/panel/SubCategory", newSubCategoryData);
      alertSuccess(response.data.success);
      return response.data; // Adjust based on your API response structure
    } catch (error) {
      alertError(error.response?.data.errors);
      return rejectWithValue(error.response?.data.errors || error.message);
    }
  }
);

const panelSlice = createSlice({
  name: "panel",
  initialState: {
    VTypeData: null, // Stores the fetched VType data
    categoryData: null, // Stores the fetched Category data
    subCategoryData: null,
    status: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
    error: null, // Stores error messages
  },
  reducers: {
    clearVTypeData(state) {
      state.VTypeData = null;
      state.status = "idle";
      state.error = null;
    },
    clearCategoryData(state) {
      state.categoryData = null;
      state.status = "idle";
      state.error = null;
    },
    clearSubCategoryData(state) {
      state.subCategoryData = null;
      state.status = "idle";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder

      // Handle fetchVTypeData
      .addCase(fetchVTypeData.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchVTypeData.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.VTypeData = action.payload;
      })
      .addCase(fetchVTypeData.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Handle updateVTypeData
      .addCase(updateVTypeData.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(updateVTypeData.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.VTypeData = { ...state.VTypeData, ...action.payload };
      })
      .addCase(updateVTypeData.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Handle addVTypeData
      // .addCase(addVTypeData.pending, (state) => {
      //   state.status = "loading";
      //   state.error = null;
      // })
      // .addCase(addVTypeData.fulfilled, (state, action) => {
      //   state.status = "succeeded";
      //   state.categoryData = [...(state.VTypeData || []), action.payload]; // Add new Category data
      // })
      // .addCase(addVTypeData.rejected, (state, action) => {
      //   state.status = "failed";
      //   state.error = action.payload;
      // })

      // Handle fetchCategoryData
      .addCase(fetchCategoryData.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchCategoryData.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.categoryData = action.payload;
      })
      .addCase(fetchCategoryData.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Handle updateCategoryData
      .addCase(updateCategoryData.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(updateCategoryData.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.categoryData = { ...state.categoryData, ...action.payload };
      })
      .addCase(updateCategoryData.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Handle addCategoryData
      .addCase(addCategoryData.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(addCategoryData.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.categoryData = [...(state.categoryData || []), action.payload]; // Add new Category data
      })
      .addCase(addCategoryData.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // handle Fetch sub-category Data
      .addCase(fetchSubCategoryData.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchSubCategoryData.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.subCategoryData = action.payload;
      })
      .addCase(fetchSubCategoryData.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // handle Update Sub-category Data
      .addCase(updateSubCategoryData.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(updateSubCategoryData.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.subCategoryData = { ...state.subCategoryData, ...action.payload };
      })
      .addCase(updateSubCategoryData.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Handle addCategoryData
      .addCase(addSubCategoryData.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(addSubCategoryData.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.subCategoryData = [...(state.subCategoryData || []), action.payload]; // Add new Category data
      })
      .addCase(addSubCategoryData.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });
  },
});

export const { clearVTypeData, clearCategoryData } = panelSlice.actions;

export default panelSlice.reducer;
